import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import {
  SiJavascript, SiReact, SiNodedotjs, SiAmazonaws, SiMongodb,
  SiPostgresql, SiHtml5, SiCss3, SiTypescript, SiKubernetes,
  SiDocker, SiJenkins, SiGit, SiAwslambda, SiWebpack,
  SiPostman, SiAmazons3, SiRedux, SiHelm, SiAmazonec2,
  SiSass, SiGithubactions, SiEslint, SiVite,
  SiNpm, SiLinux, SiNginx, SiBabel, SiJest,
} from 'react-icons/si';

const themeMap = {
  dark:       { bg: '#080808', ring: '#185FA5', ringInner: '#0C447C' },
  light:      { bg: '#f0f0f0', ring: '#0056b3', ringInner: '#003d80' },
  monochrome: { bg: '#ffffff', ring: '#444444', ringInner: '#888888' },
  nord:       { bg: '#2E3440', ring: '#88C0D0', ringInner: '#5E81AC' },
  dracula:    { bg: '#282A36', ring: '#BD93F9', ringInner: '#6272A4' },
  forest:     { bg: '#1A2E1A', ring: '#4CAF50', ringInner: '#2E7D32' },
  slate:      { bg: '#0F172A', ring: '#38BDF8', ringInner: '#0284C7' },
  gold:       { bg: '#111000', ring: '#C9A227', ringInner: '#7A5C0A' },
  crimson:    { bg: '#150808', ring: '#E63946', ringInner: '#9B1D23' },
};

const letters = [
  { char: 'i', color: '#F7DF1E' },
  { char: 'a', color: '#61DAFB' },
  { char: 'n', color: '#339933' },
  { char: 'a', color: '#FF9900' },
  { char: 's', color: '#E34F26' },
  { char: 'q', color: '#1572B6' },
  { char: 'a', color: '#3178C6' },
  { char: 'z', color: '#2496ED' },
  { char: 'i', color: '#47A248' },
];

// SVG viewBox is 680×680, center (340,340).
// Icons are placed on 3 concentric rings (r=100, r=165, r=215) so no two
// icons are closer than ~50px to each other.
//
// Ring A  r=100  8 icons every 45° (starting at 22.5°)
// Ring B  r=165  10 icons every 36° (starting at 0°)
// Ring C  r=215  11 icons every ~32.7° (starting at 16.4°)
const techIcons = [
  // ── Ring A (r=100) ──────────────────────────────────────
  { Icon: SiHelm,          x: 432, y: 302, color: '#0F1689', cls: 't3' }, // 22.5°
  { Icon: SiWebpack,       x: 378, y: 248, color: '#8DD6F9', cls: 't1' }, // 67.5°
  { Icon: SiPostman,       x: 302, y: 248, color: '#FF6C37', cls: 't5' }, // 112.5°
  { Icon: SiLinux,         x: 248, y: 302, color: '#FCC624', cls: 't2' }, // 157.5°
  { Icon: SiSass,          x: 248, y: 378, color: '#CC6699', cls: 't4' }, // 202.5°
  { Icon: SiAmazonec2,     x: 302, y: 432, color: '#FF9900', cls: 't6' }, // 247.5°
  { Icon: SiAwslambda,     x: 378, y: 432, color: '#FF9900', cls: 't2' }, // 292.5°
  { Icon: SiDocker,        x: 432, y: 378, color: '#2496ED', cls: 't4' }, // 337.5°

  // ── Ring B (r=165) ──────────────────────────────────────
  { Icon: SiNpm,           x: 505, y: 340, color: '#CB3837', cls: 't2' }, // 0°
  { Icon: SiGit,           x: 474, y: 243, color: '#F05032', cls: 't4' }, // 36°
  { Icon: SiHtml5,         x: 391, y: 183, color: '#E34F26', cls: 't6' }, // 72°
  { Icon: SiVite,          x: 289, y: 183, color: '#646CFF', cls: 't1' }, // 108°
  { Icon: SiJenkins,       x: 207, y: 243, color: '#D33833', cls: 't3' }, // 144°
  { Icon: SiRedux,         x: 175, y: 340, color: '#764ABC', cls: 't5' }, // 180°
  { Icon: SiMongodb,       x: 207, y: 437, color: '#47A248', cls: 't2' }, // 216°
  { Icon: SiCss3,          x: 289, y: 497, color: '#1572B6', cls: 't4' }, // 252°
  { Icon: SiEslint,        x: 391, y: 497, color: '#4B32C3', cls: 't1' }, // 288°
  { Icon: SiPostgresql,    x: 474, y: 437, color: '#336791', cls: 't6' }, // 324°

  // ── Ring C (r=215) ──────────────────────────────────────
  { Icon: SiNodedotjs,     x: 546, y: 279, color: '#339933', cls: 't5' }, // 16.4°
  { Icon: SiReact,         x: 481, y: 178, color: '#61DAFB', cls: 't1' }, // 49.1°
  { Icon: SiTypescript,    x: 371, y: 127, color: '#3178C6', cls: 't3' }, // 81.8°
  { Icon: SiGithubactions, x: 251, y: 144, color: '#2088FF', cls: 't6' }, // 114.5°
  { Icon: SiAmazonaws,     x: 159, y: 224, color: '#FF9900', cls: 't2' }, // 147.3°
  { Icon: SiKubernetes,    x: 125, y: 340, color: '#326CE5', cls: 't4' }, // 180°
  { Icon: SiJest,          x: 159, y: 456, color: '#C21325', cls: 't1' }, // 212.7°
  { Icon: SiNginx,         x: 251, y: 536, color: '#009639', cls: 't3' }, // 245.5°
  { Icon: SiJavascript,    x: 371, y: 553, color: '#F7DF1E', cls: 't5' }, // 278.2°
  { Icon: SiBabel,         x: 481, y: 502, color: '#F9DC3E', cls: 't2' }, // 310.9°
  { Icon: SiAmazons3,      x: 546, y: 401, color: '#FF9900', cls: 't6' }, // 343.6°
];

const piFade = keyframes`
  0%, 100% { opacity: 0.08; }
  50%       { opacity: 0.22; }
`;

const delayMap = { t1: '0s', t2: '0.5s', t3: '1s', t4: '1.5s', t5: '2s', t6: '2.5s' };

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  flex-shrink: 0;
`;

const IconsLayer = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  overflow: hidden;
  pointer-events: none;
`;

const IconDot = styled.div`
  position: absolute;
  left: ${(p) => p.$xpct}%;
  top: ${(p) => p.$ypct}%;
  transform: translate(-50%, -50%);
  line-height: 0;
  ${(p) => css`animation: ${piFade} 3s ease-in-out infinite ${delayMap[p.$cls]};`}
`;

export default function PortfolioIconV2({ size = 300, themeName, showIcons = true }) {
  const { theme: ctxTheme } = useTheme();
  const activeTheme = themeName || ctxTheme;
  const colors = themeMap[activeTheme] || themeMap.dark;

  // Scale icon size relative to component size (SVG viewBox = 680)
  const iconPx = Math.max(12, Math.round(size * 0.055));

  return (
    <Wrapper $size={size}>
      {/* ── SVG layer: background, rings, text ── */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 680 680"
        width={size}
        height={size}
        aria-label="ianasqazi portfolio logo"
        role="img"
        style={{ display: 'block' }}
      >
        <defs>
          <style>{`
            .pi2-ring { transition: stroke 0.5s ease; }
          `}</style>
        </defs>

        {/* Base fill circle */}
        <circle cx="340" cy="340" r="290" fill={colors.bg} />

        {/* Outer spinning dashed ring */}
        <circle
          cx="340" cy="340" r="262"
          fill="none"
          stroke={colors.ring}
          strokeWidth="2.5"
          strokeDasharray="14 7"
          className="pi2-ring"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 340 340"
            to="360 340 340"
            dur="14s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Inner counter-spinning dashed ring */}
        <circle
          cx="340" cy="340" r="234"
          fill="none"
          stroke={colors.ringInner}
          strokeWidth="2"
          strokeDasharray="10 6"
          className="pi2-ring"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 340 340"
            to="-360 340 340"
            dur="10s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Top and bottom accent lines */}
        <line x1="150" y1="305" x2="530" y2="305" stroke={colors.ring} strokeWidth="1" strokeLinecap="round" className="pi2-ring" opacity="0.2" />
        <line x1="150" y1="373" x2="530" y2="373" stroke={colors.ring} strokeWidth="1" strokeLinecap="round" className="pi2-ring" opacity="0.2" />

        {/* ianasqazi — per-letter tech brand colors */}
        <text
          x="340"
          y="352"
          textAnchor="middle"
          fontFamily="'Courier New', monospace"
          fontWeight="700"
          fontSize="60"
          letterSpacing="10"
        >
          {letters.map(({ char, color }, i) => (
            <tspan key={i} fill={color}>{char}</tspan>
          ))}
        </text>

        {/* Outer border ring */}
        <circle
          cx="340" cy="340" r="290"
          fill="none"
          stroke={colors.ring}
          strokeWidth="2.5"
          className="pi2-ring"
        />
      </svg>

      {/* ── Icons layer: brand icons clipped to circle ── */}
      {showIcons && (
        <IconsLayer>
          {techIcons.map(({ Icon, x, y, color, cls }, i) => (
            <IconDot
              key={i}
              $xpct={(x / 680) * 100}
              $ypct={(y / 680) * 100}
              $cls={cls}
            >
              <Icon size={iconPx} color={color} />
            </IconDot>
          ))}
        </IconsLayer>
      )}
    </Wrapper>
  );
}
